import React, { useEffect, useMemo, useRef, useState } from 'react';
import { db } from '../../../shared/storage/firebase';
import { doc, updateDoc, arrayUnion, arrayRemove, collection, getDocs, query, where, increment, writeBatch } from 'firebase/firestore';
import { Squad, User } from '../../../types';
import '../styles/ManageSquadUsersModal.css';
import { PACKS } from '../../../data/packs';
import { removeTeacherFromSquadEvents } from '../../squads/utils/calendarSync';
import ConfirmationModal from '../../squads/components/ConfirmationModal';
import { analyzeLeadTeacherAssignment, changeSquadLeadTeacher } from '../../squads/services/leadTeacherAssignment';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  squad: Squad | null;
  onSquadUpdate: (updatedSquad: Squad) => void;
};

const ManageSquadUsersModal: React.FC<Props> = ({ isOpen, onClose, squad, onSquadUpdate }) => {
  const [activeTab, setActiveTab] = useState<'teachers' | 'students'>('students');
  const [allTeachers, setAllTeachers] = useState<User[]>([]);
  const [allStudents, setAllStudents] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateStudentModalOpen, setCreateStudentModalOpen] = useState(false);
  const [newStudentForm, setNewStudentForm] = useState({
    displayName: '',
    parentEmail: '',
    count: 1,
    appendNumber: false,
  });

  const allMuses = useMemo(() => Object.values(PACKS).flatMap(pack => pack.muses), []);

  const confirmResolverRef = useRef<null | ((v: boolean) => void)>(null);
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    title: string;
    message: string;
    confirmText: string;
  }>({ open: false, title: '', message: '', confirmText: 'Confirm' });

  const promptConfirm = (opts: { title: string; message: string; confirmText: string }): Promise<boolean> => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setConfirmState({ open: true, ...opts });
    });
  };

  useEffect(() => {
    if (isOpen) {
      const fetchUsers = async () => {
        setLoading(true);
        try {
          const teachersQuery = query(collection(db, 'users'), where('role', '==', 'teacher'));
          const studentsQuery = query(collection(db, 'users'), where('role', '==', 'student'));

          const [teacherSnapshot, studentSnapshot] = await Promise.all([
            getDocs(teachersQuery),
            getDocs(studentsQuery),
          ]);

          const teachers = teacherSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })) as User[];
          const students = studentSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })) as User[];
          
          setAllTeachers(teachers);
          setAllStudents(students);
        } catch (error) {
          console.error("Failed to fetch users:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchUsers();
    }
  }, [isOpen]);

  const handleToggleUser = async (user: User, isAssigned: boolean) => {
    if (!squad) return;

    const squadRef = doc(db, 'squads', squad.id);
    const fieldToUpdate = user.role === 'teacher' ? 'teacherIds' : 'studentIds';

    try {
      const updatedSquad = {
        ...squad,
        [fieldToUpdate]: isAssigned
          ? squad[fieldToUpdate]?.filter(id => id !== user.id)
          : [...(squad[fieldToUpdate] || []), user.id],
      };

      // If removing a teacher who is currently the lead, clear regularTeacher
      const updates: any = {
        [fieldToUpdate]: isAssigned ? arrayRemove(user.id) : arrayUnion(user.id),
      };
      const removingLead = user.role === 'teacher' && isAssigned && squad.regularTeacher === user.id;
      if (removingLead) {
        updates.regularTeacher = null;
        updates.leadTeacherId = null;
        (updatedSquad as Squad).regularTeacher = undefined;
        (updatedSquad as Squad).leadTeacherId = undefined;
      }

      if (removingLead) {
        await changeSquadLeadTeacher({ squadId: squad.id, oldTeacherId: user.id, newTeacherId: null });
      } else {
        await updateDoc(squadRef, updates);
      }

      if (user.role === 'teacher' && isAssigned && !removingLead) {
        await removeTeacherFromSquadEvents({ squadId: squad.id, teacherId: user.id });
      }

      // Keep student OG level and squadIds in sync when student membership changes
      if (user.role === 'student') {
        const studentRef = doc(db, 'users', user.id);
        if (isAssigned) {
          // Removing student from this squad
          await updateDoc(studentRef, {
            squadIds: arrayRemove(squad.id),
            ogLevel: increment(-1),
          });
        } else {
          // Adding student to this squad
          await updateDoc(studentRef, {
            squadIds: arrayUnion(squad.id),
            ogLevel: increment(1),
          });
        }
      }

      onSquadUpdate(updatedSquad);
    } catch (error) {
      console.error(`Failed to update ${user.role}:`, error);
    }
  };

  const handleSetLeadTeacher = async (teacherId: string) => {
    if (!squad) return;
    if (squad.regularTeacher === teacherId) {
      // No-op if already the lead
      return;
    }

    const oldRegularTeacher = squad.regularTeacher || null;

    try {
      // Check whether the teacher has entered an availability schedule before assigning.
      const analysis = await analyzeLeadTeacherAssignment({ squad, newTeacherId: teacherId });

      if (!analysis.hasAnyWeeklyAvailabilityTemplate) {
        const ok = await promptConfirm({
          title: 'Teacher has no availability schedule',
          message:
            'This teacher has not entered any weekly availability yet.\n\n'
            + 'Assign them as lead teacher anyway?',
          confirmText: 'Assign anyway',
        });
        if (!ok) return;
      }

      await changeSquadLeadTeacher({ squadId: squad.id, oldTeacherId: oldRegularTeacher, newTeacherId: teacherId });

      const updatedSquad: Squad = {
        ...squad,
        regularTeacher: teacherId,
        leadTeacherId: teacherId,
        teacherIds: Array.from(new Set([...(squad.teacherIds || []), teacherId])),
      };

      onSquadUpdate(updatedSquad);

    } catch (error) {
      console.error('Failed to set lead teacher:', error);
    }
  };
  
  const handleCreateStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!squad || !newStudentForm.displayName.trim()) {
      void window.appAlert('Student name is required.');
      return;
    }

    setLoading(true);
    try {
      const selectedMuse = allMuses.find(m => m.name === 'Songly'); // Default muse

      const countRaw = Number((newStudentForm as any).count);
      const count = Number.isFinite(countRaw) ? Math.max(1, Math.min(50, Math.trunc(countRaw))) : 1;
      const appendNumber = Boolean((newStudentForm as any).appendNumber);
      const baseName = newStudentForm.displayName.trim();
      const parentEmail = String(newStudentForm.parentEmail || '').trim();

      const batch = writeBatch(db);
      const ids: string[] = [];
      const newStudents: User[] = [];

      for (let i = 0; i < count; i += 1) {
        const userRef = doc(collection(db, 'users'));
        const displayName =
          appendNumber && count > 1
            ? `${baseName} ${i + 1}`
            : baseName;

        ids.push(userRef.id);
        newStudents.push({ id: userRef.id, displayName, parentEmail: parentEmail || null, role: 'student', selectedMuse: selectedMuse || null } as any);

        batch.set(userRef, {
          displayName,
          parentEmail: parentEmail || null,
          role: 'student',
          ogLevel: 1,
          squadIds: [squad.id],
          museCash: 50,
          selectedMuse,
          muses: selectedMuse ? [selectedMuse] : [],
          createdAt: new Date(),
        } as any);
      }

      // Add all created students to squad in one write.
      const squadRef = doc(db, 'squads', squad.id);
      batch.update(squadRef, {
        studentIds: arrayUnion(...ids),
      } as any);

      await batch.commit();

      // Update local state
      setAllStudents(prev => [...prev, ...newStudents]);
      onSquadUpdate({ ...squad, studentIds: [...(squad.studentIds || []), ...ids] });

      setNewStudentForm({ displayName: '', parentEmail: '', count: 1, appendNumber: false });
      setCreateStudentModalOpen(false);
    } catch (error) {
      console.error("Failed to create and add student:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortUsers = (users: User[], assignedIds: string[] = []) => {
    const lowercasedSearch = searchTerm.toLowerCase();
    
    return users
      .filter(user => user.displayName?.toLowerCase().includes(lowercasedSearch))
      .sort((a, b) => {
        const aIsAssigned = assignedIds.includes(a.id);
        const bIsAssigned = assignedIds.includes(b.id);
        if (aIsAssigned && !bIsAssigned) return -1;
        if (!aIsAssigned && bIsAssigned) return 1;
        return a.displayName?.localeCompare(b.displayName || '') || 0;
      });
  };

  const filteredTeachers = useMemo(() => filterAndSortUsers(allTeachers, squad?.teacherIds), [allTeachers, squad?.teacherIds, searchTerm]);
  const filteredStudents = useMemo(() => filterAndSortUsers(allStudents, squad?.studentIds), [allStudents, squad?.studentIds, searchTerm]);

  const renderUserList = (users: User[], assignedIds: string[] = []) => {
    if (loading) {
      return <div className="user-list-loading">Loading users...</div>;
    }

    if (users.length === 0 && searchTerm) {
      return <div className="user-list-empty">No users found matching "{searchTerm}".</div>;
    }

    if (users.length === 0) {
      return <div className="user-list-empty">No users available.</div>;
    }

    return (
      <div className="user-list">
        {users.map(user => {
          const isAssigned = assignedIds.includes(user.id);
          const isTeacher = user.role === 'teacher';
          const isLead = isTeacher && squad?.regularTeacher === user.id;
          return (
            <div key={user.id} className={`user-list-item ${isAssigned ? 'assigned' : ''}`}>
              <div className="user-item-info">
                <span className="user-item-name">{user.displayName}</span>
                <span className="user-item-email">{user.email}</span>
              </div>
              <div className="user-item-actions">
                {isTeacher && isAssigned && (
                  <>
                    <button
                      className={`user-item-btn ${isLead ? 'lead-badge' : 'set-lead-btn'}`}
                      disabled={isLead}
                      onClick={() => !isLead && handleSetLeadTeacher(user.id)}
                      title={isLead ? 'Lead teacher' : 'Make lead teacher'}
                    >
                      {isLead ? 'Lead' : 'Make Lead'}
                    </button>
                  </>
                )}
                <button
                  className={`user-item-btn ${isAssigned ? 'remove-btn' : 'add-btn'}`}
                  onClick={() => handleToggleUser(user, isAssigned)}
                >
                  {isAssigned ? 'Remove' : 'Add'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="manage-users-modal">
        <div className="modal-header">
          <h3>Manage Users for <span className="squad-name-highlight">{squad?.title}</span></h3>
          <button onClick={onClose} className="close-modal-btn">&times;</button>
        </div>
        <div className="modal-body">
          <div className="tabs">
            <button
              className={`tab-btn ${activeTab === 'students' ? 'active' : ''}`}
              onClick={() => setActiveTab('students')}
            >
              Students ({squad?.studentIds?.length || 0})
            </button>
            <button
              className={`tab-btn ${activeTab === 'teachers' ? 'active' : ''}`}
              onClick={() => setActiveTab('teachers')}
            >
              Teachers ({squad?.teacherIds?.length || 0})
            </button>
          </div>
          <div className="search-bar-container">
            <input
              type="text"
              placeholder={`Search ${activeTab}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="user-search-input"
            />
          </div>
          <div className="tab-content">
            {activeTab === 'students' && (
              <>
                <button className="create-student-btn" onClick={() => setCreateStudentModalOpen(true)}>+ Create Student</button>
                {renderUserList(filteredStudents, squad?.studentIds)}
              </>
            )}
            {activeTab === 'teachers' && renderUserList(filteredTeachers, squad?.teacherIds)}
          </div>
        </div>
      </div>
      {isCreateStudentModalOpen && (
        <div className="modal-overlay" onClick={() => setCreateStudentModalOpen(false)}>
          <div className="create-student-modal" onClick={e => e.stopPropagation()}>
            <h3>Create New Student</h3>
            <form onSubmit={handleCreateStudent}>
              <div className="form-group">
                <label>Student Name</label>
                <input
                  type="text"
                  value={newStudentForm.displayName}
                  onChange={e => setNewStudentForm({ ...newStudentForm, displayName: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Parent Email</label>
                <input
                  type="email"
                  value={newStudentForm.parentEmail}
                  onChange={e => setNewStudentForm({ ...newStudentForm, parentEmail: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>How many?</label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={String(newStudentForm.count || 1)}
                  onChange={e => setNewStudentForm({ ...newStudentForm, count: Math.max(1, Math.min(50, Number(e.target.value || 1))) })}
                />
                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                  Tip: Use this to bulk-add placeholder students quickly.
                </div>
              </div>
              <div className="form-group inline-check-row">
                <input
                  id="appendNumber"
                  type="checkbox"
                  checked={Boolean(newStudentForm.appendNumber)}
                  onChange={(e) => setNewStudentForm({ ...newStudentForm, appendNumber: e.target.checked })}
                />
                <label htmlFor="appendNumber" className="inline-check-label">
                  Append number (Name 1, Name 2)
                </label>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setCreateStudentModalOpen(false)}>Cancel</button>
                <button type="submit" className="save-btn" disabled={loading}>{loading ? 'Creating...' : 'Create & Add'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmState.open}
        onClose={() => {
          setConfirmState(s => ({ ...s, open: false }));
          const r = confirmResolverRef.current;
          confirmResolverRef.current = null;
          r?.(false);
        }}
        onConfirm={() => {
          setConfirmState(s => ({ ...s, open: false }));
          const r = confirmResolverRef.current;
          confirmResolverRef.current = null;
          r?.(true);
        }}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText="Cancel"
      />
    </div>
  );
};

export default ManageSquadUsersModal;
